import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useSocket } from '../hooks/useSocket';
import { messagesAPI } from '../api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { onNewMessage } = useSocket();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  const sellRef = useRef(null);
  const [search, setSearch] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (sellRef.current && !sellRef.current.contains(e.target)) setSellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    messagesAPI.getUnreadCount().then(r => setUnreadCount(r.data.count || 0)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const unsub = onNewMessage((msg) => {
      if (String(msg.sender_id) !== String(user.id)) setUnreadCount(c => c + 1);
    });
    return unsub;
  }, [user, onNewMessage]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    navigate(`/services?${params.toString()}`);
    setMenuOpen(false);
  };

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/products', label: 'Products' },
    { path: '/providers', label: 'Providers' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`sticky top-0 z-50 bg-white border-b border-gray-200 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo + Nav links */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-1 group flex-shrink-0">
              {siteSettings?.site_logo ? (
                <img src={siteSettings.site_logo} alt={siteSettings?.site_name || 'TopInfo Rwanda'} className="h-8 w-auto object-contain" />
              ) : (
                <>
                  <span className="text-xl font-extrabold text-primary-600 tracking-tight">TopInfo</span>
                  <span className="text-[9px] bg-primary-600 text-white px-1.5 py-0.5 rounded font-bold">.rw</span>
                </>
              )}
            </Link>
            <div className="hidden md:flex items-center gap-0.5">
              {navLinks.map(link => (
                <Link key={link.path} to={link.path}
                  className={`px-3 py-1.5 text-sm font-medium transition-all duration-200 rounded-lg ${
                    isActive(link.path)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                  }`}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-1.5">
            {user ? (
              <>
                {/* Messages */}
                <Link to="/messages" onClick={() => setUnreadCount(0)}
                  className="hidden md:flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-gray-50 transition-all relative" title="Messages">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-primary-600 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                </Link>
                {/* Favorites */}
                <Link to="/favorites" className="hidden md:flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-gray-50 transition-all" title="Saved">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                </Link>
                {/* Notifications */}
                <Link to="/notifications" className="hidden md:flex items-center justify-center w-9 h-9 rounded-lg text-gray-500 hover:text-primary-600 hover:bg-gray-50 transition-all" title="Notifications">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                </Link>
                {/* Profile dropdown */}
                <div className="relative hidden md:block" ref={profileRef}>
                  <button onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-all">
                    <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold text-xs">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {profileOpen && (
                    <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl shadow-black/10 border border-gray-100 py-2 z-50 animate-scale-in">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="font-semibold text-sm text-gray-900">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                      </div>
                      {[
                        { to: '/dashboard', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6z', label: 'My Dashboard' },
                        { to: `/providers/${user.id}`, icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', label: 'My Profile' },
                        { to: '/favorites', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', label: 'Saved Ads' },
                        { to: '/orders', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z', label: 'Orders' },
                        { to: '/bookings', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', label: 'Bookings' },
                        { to: '/profile', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', label: 'Settings' },
                      ].map(item => (
                        <Link key={item.to} to={item.to}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)}>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={item.icon} /></svg>
                          {item.label}
                        </Link>
                      ))}
                      {user.role === 'admin' && (
                        <Link to="/ahupa" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition" onClick={() => setProfileOpen(false)}>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          Admin Panel
                        </Link>
                      )}
                      <div className="border-t border-gray-100 my-1" />
                      <button onClick={() => { setProfileOpen(false); logout(); navigate('/'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
                {/* Start Selling */}
                <div className="relative hidden md:block ml-1" ref={sellRef}>
                  <button onClick={() => setSellOpen(!sellOpen)}
                    className="inline-flex items-center gap-1.5 bg-primary-600 text-white font-bold px-5 py-2 rounded-lg text-sm hover:bg-primary-700 transition-all duration-200 shadow-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                    Start Selling
                  </button>
                  {sellOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl shadow-black/10 border border-gray-100 py-2 z-50 animate-scale-in">
                      <Link to="/services/create" onClick={() => setSellOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition rounded-lg mx-1">
                        <span className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center text-lg">🔧</span>
                        <div><p className="font-medium">Post a Service</p><p className="text-xs text-gray-400">Offer your skills</p></div>
                      </Link>
                      <Link to="/products/create" onClick={() => setSellOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition rounded-lg mx-1">
                        <span className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center text-lg">📦</span>
                        <div><p className="font-medium">Sell a Product</p><p className="text-xs text-gray-400">List an item</p></div>
                      </Link>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-primary-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-all">Sign In</Link>
                <Link to="/register" className="text-sm font-semibold text-primary-600 border border-primary-200 bg-primary-50 px-4 py-1.5 rounded-lg hover:bg-primary-100 transition-all">Register</Link>
                <Link to="/login"
                  className="ml-1 inline-flex items-center gap-1.5 bg-primary-600 text-white font-bold px-5 py-2 rounded-lg text-sm hover:bg-primary-700 transition-all shadow-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                  Start Selling
                </Link>
              </div>
            )}
            {/* Mobile hamburger */}
            <button className="md:hidden p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition" onClick={() => setMenuOpen(!menuOpen)}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b border-gray-200 shadow-lg animate-slide-up">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            {/* Mobile search */}
            <form onSubmit={handleSearch} className="flex mb-3">
              <div className="flex w-full bg-gray-100 border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500/30 focus-within:border-primary-400 transition-all">
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search on TopInfo..."
                  className="flex-1 h-10 bg-transparent px-4 text-sm focus:outline-none placeholder-gray-400" />
                <button type="submit" className="h-10 px-4 bg-primary-600 text-white">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </button>
              </div>
            </form>
            <div className="flex flex-col text-sm">
              {navLinks.map(link => (
                <Link key={link.path} to={link.path}
                  className={`px-4 py-3 rounded-lg font-medium transition ${isActive(link.path) ? 'text-primary-600 bg-primary-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  onClick={() => setMenuOpen(false)}>
                  {link.label}
                </Link>
              ))}
              {user ? (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link to="/dashboard" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>My Dashboard</Link>
                  <Link to={`/providers/${user.id}`} className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>My Profile</Link>
                  <Link to="/messages" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg flex items-center justify-between" onClick={() => { setMenuOpen(false); setUnreadCount(0); }}>
                    Messages
                    {unreadCount > 0 && <span className="bg-primary-600 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">{unreadCount > 99 ? '99+' : unreadCount}</span>}
                  </Link>
                  <Link to="/favorites" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Saved Ads</Link>
                  <Link to="/orders" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Orders</Link>
                  <Link to="/bookings" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Bookings</Link>
                  <Link to="/notifications" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Notifications</Link>
                  <Link to="/profile" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Settings</Link>
                  {user.role === 'admin' && <Link to="/ahupa" className="px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setMenuOpen(false)}>Admin Panel</Link>}
                  <div className="border-t border-gray-100 my-2" />
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/services/create" onClick={() => setMenuOpen(false)} className="py-2.5 bg-primary-600 text-white rounded-lg text-center font-semibold text-sm">🔧 Service</Link>
                    <Link to="/products/create" onClick={() => setMenuOpen(false)} className="py-2.5 bg-primary-600 text-white rounded-lg text-center font-semibold text-sm">📦 Product</Link>
                  </div>
                  <button onClick={() => { setMenuOpen(false); logout(); navigate('/'); }} className="mt-3 py-2.5 text-red-600 font-medium text-center rounded-lg hover:bg-red-50 transition">Sign Out</button>
                </>
              ) : (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <Link to="/login" className="px-4 py-3 text-gray-700 font-medium rounded-lg hover:bg-gray-50" onClick={() => setMenuOpen(false)}>Sign In</Link>
                  <Link to="/register" className="mt-1 py-2.5 bg-primary-600 text-white rounded-lg text-center font-semibold" onClick={() => setMenuOpen(false)}>Create Account</Link>
                  <Link to="/login" className="mt-2 py-2.5 bg-white border-2 border-primary-600 text-primary-600 rounded-lg text-center font-bold" onClick={() => setMenuOpen(false)}>+ Start Selling</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
